import { useMemo, useState } from 'react'

import { t } from './i18n'

/**
 * Celebrations rendered over the whole room.
 *
 * Individual reactions are NOT here — they belong to whoever sent them and are
 * drawn on that person's tile (see Stage). A celebration is different: the teacher
 * is congratulating the class, so it genuinely is room-wide.
 *
 * `pointer-events: none` throughout: nothing here may ever intercept a click
 * meant for a control. Animations use transform and opacity only, so they stay on
 * the compositor and cannot cause layout work on a low-end phone mid-lesson.
 *
 * Note these do NOT appear in the recording — egress composites the room with
 * LiveKit's own template, not this app. That is a deliberate accepted decision:
 * recordings capture video and audio only.
 */

export function ReactionLayer({ celebrating }: { celebrating: boolean }) {
  if (!celebrating) return null

  return (
    <div className="reaction-layer" aria-live="polite" aria-atomic="false">
      <Confetti />
    </div>
  )
}

/**
 * The celebration, choreographed in four cheap layers.
 *
 * CSS-only, deliberately. A canvas particle system would be smoother on a laptop
 * and markedly worse on the phones most of our students use: it needs the main
 * thread every frame, and the main thread is already decoding up to twenty video
 * streams. Every layer here animates transform and opacity only, which the
 * compositor runs on its own.
 *
 * The count matters as much as the technique. This used to throw 60 confetti
 * strips plus 14 emoji — 74 simultaneously animating elements, each of the emoji
 * carrying a drop-shadow FILTER, which costs a pass per element per frame. It is
 * now 26 + 10 with no filters anywhere, and it reads as *more* of an event,
 * because the pieces are staggered into a sequence instead of all arriving at
 * once: bloom (impact) → burst (throw) → ribbons (fall) → banner (land).
 */
function Confetti() {
  // Read synchronously via the initialiser rather than in an effect: setting it
  // after mount meant the confetti and burst painted one frame of full motion
  // before being removed, which is exactly the flash the setting exists to avoid.
  const [reduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  const pieces = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        id: i,
        // Spread across the width in even bands with jitter inside each, rather
        // than pure random: 26 random values leave visible bald patches, and the
        // eye reads a gap as "it broke" rather than "it is random".
        left: (i / 26) * 100 + Math.random() * (100 / 26),
        delay: Math.random() * 0.55,
        duration: 2.5 + Math.random() * 1.5,
        rotation: 220 + Math.random() * 500,
        // Sideways flutter amplitude, alternating direction per piece so the
        // field as a whole never leans one way.
        sway: (i % 2 === 0 ? 1 : -1) * (14 + Math.random() * 26),
        colour: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
        size: 7 + Math.random() * 7,
      })),
    [],
  )

  return (
    <>
      {/* Warm light blooming from the centre — the "why" of the confetti. */}
      <div className="celebration-bloom" aria-hidden="true" />

      <div className="celebration-banner">
        <span>🎉</span>
        {t('room.wellDone')}
      </div>

      <Burst />

      {/* The only layer with no still equivalent: a ribbon's whole point is the
          fall, so under reduced motion it is simply not rendered rather than
          parked somewhere pretending. */}
      {reduced ? null : (
        <div className="confetti" aria-hidden="true">
          {pieces.map((piece) => (
            <i
              key={piece.id}
              style={
                {
                  left: `${piece.left}%`,
                  background: piece.colour,
                  width: `${piece.size}px`,
                  height: `${piece.size * 0.4}px`,
                  animationDelay: `${piece.delay}s`,
                  animationDuration: `${piece.duration}s`,
                  '--spin': `${piece.rotation}deg`,
                  '--sway': `${Math.round(piece.sway)}px`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
    </>
  )
}

/* Brand palette, so a celebration still looks like Everhall. */
const CONFETTI_COLOURS = ['#1F2A5A', '#2F3F85', '#F4A340', '#3FB68B', '#E0566B', '#FAF8F3']

const BURST_EMOJI = ['🎉', '⭐', '✨', '🎊', '👏', '🏆', '💫']

/**
 * A scatter of emoji thrown outward from the centre.
 *
 * Each one gets its own angle, distance, size, spin and delay, so a celebration
 * never plays the same way twice — the point of a reward is that it feels like an
 * event, and an identical animation stops feeling like one by the third time a
 * child sees it.
 *
 * Ten pieces, down from fourteen, and the angles are now evenly spaced with
 * jitter instead of fully random: an even ring of ten reads as a deliberate
 * burst, where fourteen random angles clumped and left holes. Transform and
 * opacity only.
 */
function Burst() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => {
        // Half a segment of jitter: enough that it never looks stamped, not
        // enough to let two pieces overlap into one blob.
        const angle = (i / 10) * Math.PI * 2 + (Math.random() - 0.5) * (Math.PI / 10)
        const distance = 110 + Math.random() * 170
        return {
          id: i,
          emoji: BURST_EMOJI[i % BURST_EMOJI.length],
          dx: Math.round(Math.cos(angle) * distance),
          dy: Math.round(Math.sin(angle) * distance) - 40,
          size: 22 + Math.round(Math.random() * 24),
          spin: Math.round(Math.random() * 620 - 310),
          delay: Number((Math.random() * 0.3).toFixed(2)),
          duration: Number((1.6 + Math.random() * 1.2).toFixed(2)),
        }
      }),
    [],
  )

  return (
    <div className="burst" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="burst__piece"
          style={
            {
              '--dx': `${p.dx}px`,
              '--dy': `${p.dy}px`,
              '--spin': `${p.spin}deg`,
              fontSize: `${p.size}px`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            } as React.CSSProperties
          }
        >
          {p.emoji}
        </span>
      ))}
    </div>
  )
}
